/*
 * Audience Data Synthesis — presentation layer.
 *
 * The canonical audience-intelligence record is prose. A manual Audience
 * Vectoring upload is usually one platform heading followed by an unlabeled
 * analysis, so the governed payload carries that same analysis through several
 * fields at once: `card.summary` (the composed client narrative),
 * `primaryAudienceNarrative` (the analysis body) and `audienceSummary` (its
 * opening). Rendering those fields as separate paragraphs printed the same
 * intelligence two and three times.
 *
 * This module does two things, and deliberately nothing else:
 *
 *   1. Resolves ONE canonical body out of however many payload properties
 *      carry it. Deduplication is literal containment only — a candidate is
 *      dropped solely when another kept candidate carries its full text
 *      verbatim. Two genuinely different bodies are both preserved.
 *
 *   2. Structures that body into a readable hierarchy using generic audience
 *      vocabulary — age, gender, device, geography, attention, response — and
 *      structural cues that hold for any campaign on any platform. No campaign
 *      name, platform name, brand or metric value is matched anywhere here.
 *
 * What it never does: rewrite, summarize, translate or infer. Every string it
 * emits is a verbatim span of the canonical record, and every sentence of that
 * record is emitted exactly once — a sentence promoted into a signal card is
 * removed from the analysis body, and anything no rule claims falls through to
 * the analysis in its original order. Nothing is dropped for not fitting.
 *
 * It is a pure function of the payload: no fetching, no model call, no writes.
 * The surface it feeds stays read-only.
 */

/* Sentences a signal card may carry before the rest fall through to the
 * analysis body. A card is a scannable takeaway plus one piece of evidence,
 * not a second copy of the analysis — and a low cap keeps the cards in a row
 * close enough in height that the grid does not read as broken. */
const MAX_SENTENCES_PER_SIGNAL = 2;

/* Sentences per analysis paragraph. Long enough to read as an argument, short
 * enough that the eye finds its way back after a line break. */
const SENTENCES_PER_PARAGRAPH = 3;

/*
 * Signal taxonomy. Generic audience-analysis vocabulary only — these words
 * describe how any audience is discussed, not what any particular campaign
 * found. Order is the render order and the deterministic tie-break.
 */
export const AUDIENCE_SIGNAL_DEFINITIONS = [
  {
    key: "age",
    label: "Age",
    pattern:
      /\b(age|ages|aged|age-\w+|\d{2}\s*[-–—]\s*\d{2}|\d{2}\+|generation|younger|older|adults?|demographics? chart)\b/i,
  },
  {
    key: "gender",
    label: "Gender",
    pattern: /\b(gender|male|female|men|women|male-\w+|female-\w+)\b/i,
  },
  {
    key: "device",
    label: "Device",
    pattern:
      /\b(device|devices|mobile|handset|phones?|desktop|computers?|tablets?|tv screens?|connected tv|ctv|cross-device|lean-back)\b/i,
  },
  {
    key: "geography",
    label: "Geography",
    pattern:
      /\b(geograph\w*|market|markets|cit(?:y|ies)|states?|regions?|dma|nationwide|zip|postcode|locally|footprint)\b/i,
  },
  {
    key: "attention",
    label: "Engagement / Attention",
    pattern:
      /\b(engagement|engaged|attention|complet\w+|played to|view rate|view-rate|watch\w*|viewing|views?|viewers?|vcr|dwell)\b/i,
  },
  {
    key: "response",
    label: "Response Behavior",
    pattern:
      /\b(click|clicks|clicking|ctr|conversion\w*|convert\w*|respond\w*|response|action-oriented|off-platform|traffic)\b/i,
  },
];

/*
 * Advisory phrasing. A recommendation is a statement about what to DO — the
 * bare noun "action" is not one, which is why "highly mobile when taking
 * action" is an observation and stays in the analysis.
 */
const RECOMMENDATION_PATTERN =
  /\b(recommend(?:s|ed|ation|ations)?|i would|we would|we should|you should|should (?:be|assume|not|therefore|absolutely|still|keep|hold)|keep\b[^.]{0,60}\bresponsible|next step|going forward|optimi[sz]ation (?:guidance|direction)|prioriti[sz]e|treat(?:ed)? as|watch\b|monitor\b|avoid\b|don't\b|do not\b)\b/i;

/*
 * Interpretive phrasing — where the analyst moves from what was observed to
 * what it means. These sentences carry the reasoning, so they are separated
 * from the observations rather than left buried among them.
 */
const INTERPRETATION_PATTERN =
  /\b(that (?:means|tells us|suggests|implies)|in other words|which (?:means|suggests|implies)|so we|the (?:biggest|strongest|most) [\w\s-]{0,24}finding|two (?:different|distinct|separate)[\w\s/-]{0,30}(?:audiences?|behaviou?rs?|modes?)|audience modes?|i would (?:therefore )?be cautious|there is a significant caveat|strategically|healthy structure)\b/i;

/*
 * A labeled mode definition — "<label> = <description>" or "<label>: <desc>".
 * A structural pattern, not a vocabulary one: it recognises that the analyst
 * named and defined a mode, whatever the mode happens to be called.
 */
const MODE_DEFINITION_PATTERN = /^(.{3,80}?)\s*=\s*(.{10,})$/;

/* Caution phrasing, surfaced beside a geographic read so a directional
 * observation is not mistaken for a definitive one. */
const CAUTION_PATTERN =
  /\b(caution|cautious|caveat|normali[sz]\w*|directional|not necessarily|wouldn't|would not|before|until|premature\w*|too early)\b/i;

function normalizeText(value) {
  return String(value ?? "").trim();
}

/*
 * Comparable form for containment checks. Case and whitespace only —
 * punctuation and wording are preserved so containment stays literal. This is
 * deliberately NOT fuzzy: a fragment is only ever treated as a duplicate when
 * another fragment carries its exact text.
 */
function toComparable(value) {
  return normalizeText(value).replace(/\s+/g, " ").toLowerCase();
}

function carriesVerbatim(outer, inner) {
  const haystack = toComparable(outer);
  const needle = toComparable(inner);
  if (!haystack || !needle) return false;
  return haystack.includes(needle);
}

/* Sentence identity for duplicate detection: trailing punctuation and casing
 * ignored, everything else exact. */
function sentenceKey(value) {
  return toComparable(value).replace(/[.!?\s]+$/, "");
}

function splitSentences(value) {
  return normalizeText(value)
    .replace(/\s+/g, " ")
    .split(/(?<=[.!?])\s+/)
    .map((entry) => normalizeText(entry))
    .filter(Boolean);
}

function toStringList(value) {
  if (!Array.isArray(value)) return [];
  return value.map((entry) => normalizeText(entry)).filter(Boolean);
}

/*
 * Resolves the canonical body or bodies from every payload property that could
 * carry the synthesis.
 *
 * Candidates are considered widest-first, so when two properties carry the
 * same intelligence the fuller one is kept and the shorter is recognised as
 * already contained in it. A candidate that no kept body carries verbatim is
 * genuinely different intelligence and is kept as its own block.
 */
export function resolveCanonicalBodies(candidates = []) {
  const ordered = candidates
    .map((entry) => normalizeText(entry))
    .filter(Boolean)
    .map((text, index) => ({ text, index }))
    .sort((a, b) => b.text.length - a.text.length || a.index - b.index);

  const kept = [];
  ordered.forEach((candidate) => {
    if (kept.some((entry) => carriesVerbatim(entry.text, candidate.text))) return;
    kept.push(candidate);
  });

  // Restore the payload's own field order for anything genuinely distinct.
  return kept.sort((a, b) => a.index - b.index).map((entry) => entry.text);
}

/*
 * Collapses sentences repeated verbatim inside the canonical body. Legacy
 * records were persisted with their opening restated, so the stored prose
 * itself contains the repeat. Only an exact sentence match is collapsed — a
 * sentence that merely overlaps another is different analysis and is kept.
 */
/*
 * A sentence written as "<Label>: <restated sentence>". Legacy records were
 * composed by appending the derived guidance under a label, so the body ends
 * with a labeled copy of a sentence it already contains.
 */
const LABELED_RESTATEMENT_PATTERN = /^[A-Z][A-Za-z /&-]{2,40}:\s+(.+)$/;

function dedupeSentences(sentences) {
  const seen = new Set();
  const unique = sentences.filter((sentence) => {
    const key = sentenceKey(sentence);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  /*
   * Second pass: drop a labeled sentence whose payload is EXACTLY a sentence
   * already kept. The label alone defeats the identity check above, which is
   * how "Optimization guidance: <the opening sentence>" survived beside the
   * opening sentence itself. Exact-match only — a labeled sentence that merely
   * discusses the same subject is different analysis and stays.
   */
  return unique.filter((sentence) => {
    const match = normalizeText(sentence).match(LABELED_RESTATEMENT_PATTERN);
    if (!match) return true;
    const payloadKey = sentenceKey(match[1]);
    if (!payloadKey || payloadKey === sentenceKey(sentence)) return true;
    return !seen.has(payloadKey);
  });
}

function countPatternHits(sentence, pattern) {
  const global = new RegExp(pattern.source, `${pattern.flags.replace(/g/g, "")}g`);
  const matches = sentence.match(global);
  return matches ? matches.length : 0;
}

/*
 * Assigns a sentence to the signal it is most about — the definition with the
 * most vocabulary hits, ties broken by the fixed taxonomy order so the same
 * record always structures identically.
 */
function classifySignal(sentence) {
  let best = null;
  let bestHits = 0;
  AUDIENCE_SIGNAL_DEFINITIONS.forEach((definition) => {
    const hits = countPatternHits(sentence, definition.pattern);
    if (hits > bestHits) {
      best = definition;
      bestHits = hits;
    }
  });
  return bestHits > 0 ? best : null;
}

function buildParagraphs(sentences) {
  const paragraphs = [];
  for (let index = 0; index < sentences.length; index += SENTENCES_PER_PARAGRAPH) {
    paragraphs.push(sentences.slice(index, index + SENTENCES_PER_PARAGRAPH).join(" "));
  }
  return paragraphs;
}

/*
 * Reads a sentence the analyst wrote as a named mode definition —
 * "<label> = <description>". Structural, not vocabulary: it recognises that a
 * mode was named and defined, whatever it was named. Returns null when the
 * sentence is not one, so nothing is forced into a shape the record lacks.
 */
function readModeDefinition(sentence) {
  const match = normalizeText(sentence).match(MODE_DEFINITION_PATTERN);
  if (!match) return null;
  const label = normalizeText(match[1]).replace(/^[-•*\s]+/, "");
  const body = normalizeText(match[2]);
  if (!label || !body) return null;
  return { label, body };
}

/*
 * Builds the full report view model.
 *
 * Structured canonical fields win wherever the record actually has them: a
 * record that stored `ageSignals` is read from `ageSignals`, and prose
 * classification only fills the dimensions the record left to prose. That is
 * what keeps this a presentation layer over the canonical model rather than a
 * parser standing in for one.
 */
export function resolveCanonicalSentences(payload = {}) {
  const bodies = resolveCanonicalBodies([
    payload.summary,
    payload.narrative,
    payload.audienceSummary,
    payload.clientFacingPrompt,
  ]);
  return {
    bodies,
    sentences: dedupeSentences(bodies.flatMap((body) => splitSentences(body))),
  };
}

/*
 * The single canonical synthesis body as one readable string.
 *
 * For surfaces that present the synthesis as prose rather than as a structured
 * report — the Cross-Platform per-platform entries and unified thesis — this is
 * the same resolution the structured report is built on. Sharing it is what
 * stops the identical legacy record from reading clean on one surface and
 * restating itself on another.
 */
export function resolveCanonicalSynthesisText(payload = {}) {
  return resolveCanonicalSentences(payload).sentences.join(" ") || null;
}

export function buildAudienceIntelligenceReport(payload = {}) {
  const { bodies, sentences: allSentences } = resolveCanonicalSentences(payload);

  const structuredByKey = {
    age: toStringList(payload.ageSignals),
    gender: toStringList(payload.genderSignals),
    device: toStringList(payload.deviceSignals),
    geography: toStringList(payload.geographySignals),
    attention: [],
    response: [],
  };

  const declaredRecommendation = normalizeText(payload.recommendedAction);

  /*
   * Named mode definitions are recognised first, over the whole body. They are
   * structurally distinct — the analyst labeled and defined a mode — so they
   * must not be absorbed into the executive read or read as an ordinary device
   * or response observation. A lone definition is not a contrast, so two are
   * required before they become mode cards.
   */
  const modeSentences = allSentences.filter((sentence) => readModeDefinition(sentence));
  const modes = modeSentences.length >= 2 ? modeSentences.map(readModeDefinition) : [];
  const modeKeys = new Set(modes.length ? modeSentences.map(sentenceKey) : []);
  const narratable = allSentences.filter((sentence) => !modeKeys.has(sentenceKey(sentence)));

  /* The lead conclusion. The opening of an analysis is its conclusion; a very
   * short opener carries the following sentence with it so the executive read
   * is a statement rather than a fragment. */
  const headlineSentences = [];
  if (narratable.length) {
    headlineSentences.push(narratable[0]);
    if (narratable[0].length < 80 && narratable[1]) {
      headlineSentences.push(narratable[1]);
    }
  }
  const headlineKeys = new Set(headlineSentences.map(sentenceKey));

  const remaining = narratable.filter((sentence) => !headlineKeys.has(sentenceKey(sentence)));

  /* Recommendations first: an advisory sentence is a recommendation wherever it
   * sits, and must not be consumed by a signal card. */
  const recommendations = [];
  const afterRecommendations = [];
  remaining.forEach((sentence) => {
    if (RECOMMENDATION_PATTERN.test(sentence)) recommendations.push(sentence);
    else afterRecommendations.push(sentence);
  });

  /* Interpretation next — the reasoning layer, separated from observation. */
  const interpretationSentences = [];
  const observations = [];
  afterRecommendations.forEach((sentence) => {
    if (INTERPRETATION_PATTERN.test(sentence)) interpretationSentences.push(sentence);
    else observations.push(sentence);
  });

  /* Observations become signal evidence, capped so the cards stay scannable.
   * Anything past the cap keeps its place in the analysis body. */
  const claimedByKey = {};
  AUDIENCE_SIGNAL_DEFINITIONS.forEach((definition) => {
    claimedByKey[definition.key] = [];
  });
  const analysisSentences = [];

  observations.forEach((sentence) => {
    const definition = classifySignal(sentence);
    if (!definition) {
      analysisSentences.push(sentence);
      return;
    }
    /*
     * A record that STORED this dimension renders the stored lines below, and
     * the prose sentence about it is then never printed as evidence. Claiming
     * it here would remove it from the analysis body and render it nowhere —
     * governed sentences were disappearing from the synthesis exactly this
     * way, which is a content loss rather than a layout choice. A dimension
     * the record did not store still takes its evidence from the prose.
     */
    const storedForKey = (structuredByKey[definition.key] || []).length > 0;
    if (storedForKey || claimedByKey[definition.key].length >= MAX_SENTENCES_PER_SIGNAL) {
      analysisSentences.push(sentence);
      return;
    }
    claimedByKey[definition.key].push(sentence);
  });

  const allSignals = AUDIENCE_SIGNAL_DEFINITIONS.map((definition) => {
    const structured = structuredByKey[definition.key] || [];
    const fromProse = claimedByKey[definition.key] || [];
    const lines = structured.length ? structured : fromProse;
    if (!lines.length) return null;
    return {
      key: definition.key,
      label: definition.label,
      takeaway: lines[0],
      evidence: lines.slice(1),
      /* Records that stored this dimension are read from the record. */
      fromStructuredField: structured.length > 0,
    };
  }).filter(Boolean);

  /*
   * Geography gets its own section, so it is REMOVED from the signal grid
   * rather than rendered in both — the section and the card would otherwise be
   * two copies of the same finding. Any caution the analyst attached to the
   * geographic read is kept beside it rather than lost among the actions.
   */
  const geographySignal = allSignals.find((signal) => signal.key === "geography") || null;
  const signals = allSignals.filter((signal) => signal.key !== "geography");
  const geographyDefinition = AUDIENCE_SIGNAL_DEFINITIONS.find(
    (definition) => definition.key === "geography"
  );
  const geographyCaution = geographySignal
    ? interpretationSentences.find(
        (sentence) =>
          CAUTION_PATTERN.test(sentence) && geographyDefinition.pattern.test(sentence)
      ) || null
    : null;
  const geography = geographySignal
    ? {
        takeaway: geographySignal.takeaway,
        evidence: geographySignal.evidence,
        caution: geographyCaution,
      }
    : null;

  const orderedRecommendations = [...recommendations];
  /*
   * A record that declares its own recommended action leads with it — but only
   * when the canonical body does not already carry that sentence somewhere.
   * Legacy records stored the opening OBSERVATION as the recommended action,
   * so unconditionally prepending it printed the synthesis's first line a
   * second time under a different heading.
   */
  if (
    declaredRecommendation &&
    !allSentences.some((sentence) => carriesVerbatim(sentence, declaredRecommendation))
  ) {
    orderedRecommendations.unshift(declaredRecommendation);
  }

  return {
    executiveRead: {
      headline: headlineSentences.join(" ") || null,
      /* Which dimensions this synthesis actually covers — a scannable index of
       * the record, never a conclusion drawn on its behalf. Geography is
       * counted here even though it renders as its own section. */
      coverage: allSignals.map((signal) => signal.label),
    },
    signals,
    interpretation: {
      modes,
      /* The geographic caution renders in the geography section, so it is not
       * also left in the interpretation prose. */
      prose: interpretationSentences.filter((sentence) => sentence !== geographyCaution),
    },
    geography,
    recommendations: orderedRecommendations,
    analysis: buildParagraphs(analysisSentences),
    strongestSegments: toStringList(payload.strongestSegments),
    emergingSegments: toStringList(payload.emergingSegments),
    /* True when more than one payload property carried genuinely different
     * intelligence — both are rendered, neither is hidden. */
    distinctBodyCount: bodies.length,
  };
}

/*
 * Every sentence the report will render, for assertions that no canonical
 * content was dropped between the payload and the presentation.
 */
export function collectReportSentences(report) {
  if (!report) return [];
  return [
    report.executiveRead?.headline || "",
    ...report.signals.flatMap((signal) => [signal.takeaway, ...signal.evidence]),
    ...(report.geography
      ? [report.geography.takeaway, ...report.geography.evidence, report.geography.caution || ""]
      : []),
    ...report.interpretation.modes.map((mode) => `${mode.label} = ${mode.body}`),
    ...report.interpretation.prose,
    ...report.recommendations,
    ...report.analysis,
  ]
    .flatMap((entry) => splitSentences(entry))
    .filter(Boolean);
}
